/**
 * Word-level text diff.
 *
 * Written here rather than pulled from a package because the alignment the rest
 * of this module needs is block-level (by id) — this only has to handle a single
 * paragraph at a time, where a plain LCS is both fast enough and easier to reason
 * about than a Myers implementation.
 *
 * Tokens keep their whitespace, so joining every run's text reproduces the input
 * exactly on each side. That matters for Hebrew: reflowing whitespace around an
 * RTL insertion is how a diff view ends up showing changes that never happened.
 */

import type { DiffRun } from './types'

/** Above this many tokens on either side the LCS table stops being worth it. */
const LCS_TOKEN_LIMIT = 1500

/** Split into words and whitespace runs, both preserved as tokens. */
function tokenizeWords(text: string): string[] {
  return text.match(/\s+|\S+/g) ?? []
}

/** Number of non-whitespace tokens — what the change map counts. */
export function countWords(text: string): number {
  let count = 0
  for (const token of tokenizeWords(text)) {
    if (token.trim().length > 0) {
      count++
    }
  }
  return count
}

function pushRun(runs: DiffRun[], op: DiffRun['op'], text: string): void {
  if (text.length === 0) {
    return
  }
  const last = runs[runs.length - 1]
  if (last && last.op === op) {
    last.text += text
    return
  }
  runs.push({ op, text })
}

/**
 * Word-level diff of two strings.
 *
 * Common prefix and suffix are trimmed first — for ordinary prose editing that
 * leaves a handful of tokens for the quadratic step, so the token limit below is
 * only ever reached by a wholesale rewrite, where a coarse replace reads better
 * than a shredded word-by-word diff anyway.
 */
export function diffWords(before: string, after: string): DiffRun[] {
  if (before === after) {
    return before.length > 0 ? [{ op: 'same', text: before }] : []
  }

  const beforeTokens = tokenizeWords(before)
  const afterTokens = tokenizeWords(after)

  let start = 0
  while (start < beforeTokens.length && start < afterTokens.length && beforeTokens[start] === afterTokens[start]) {
    start++
  }

  let end = 0
  while (
    end < beforeTokens.length - start &&
    end < afterTokens.length - start &&
    beforeTokens[beforeTokens.length - 1 - end] === afterTokens[afterTokens.length - 1 - end]
  ) {
    end++
  }

  const beforeMid = beforeTokens.slice(start, beforeTokens.length - end)
  const afterMid = afterTokens.slice(start, afterTokens.length - end)

  const runs: DiffRun[] = []
  pushRun(runs, 'same', beforeTokens.slice(0, start).join(''))

  if (beforeMid.length > LCS_TOKEN_LIMIT || afterMid.length > LCS_TOKEN_LIMIT) {
    pushRun(runs, 'delete', beforeMid.join(''))
    pushRun(runs, 'insert', afterMid.join(''))
  } else {
    for (const run of diffTokensByLcs(beforeMid, afterMid)) {
      pushRun(runs, run.op, run.text)
    }
  }

  pushRun(runs, 'same', beforeTokens.slice(beforeTokens.length - end).join(''))
  return runs
}

/** Classic LCS table + backtrack, emitting deletes before inserts at each split. */
function diffTokensByLcs(before: string[], after: string[]): DiffRun[] {
  const m = before.length
  const n = after.length

  if (m === 0 || n === 0) {
    const runs: DiffRun[] = []
    pushRun(runs, 'delete', before.join(''))
    pushRun(runs, 'insert', after.join(''))
    return runs
  }

  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (before[i] === after[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1])
      }
    }
  }

  const runs: DiffRun[] = []
  let i = 0
  let j = 0
  while (i < m && j < n) {
    if (before[i] === after[j]) {
      pushRun(runs, 'same', before[i])
      i++
      j++
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      pushRun(runs, 'delete', before[i])
      i++
    } else {
      pushRun(runs, 'insert', after[j])
      j++
    }
  }
  while (i < m) {
    pushRun(runs, 'delete', before[i])
    i++
  }
  while (j < n) {
    pushRun(runs, 'insert', after[j])
    j++
  }
  return runs
}

/** Rough 0–1 similarity of two strings, by shared word tokens. Used for block matching. */
export function wordSimilarity(a: string, b: string): number {
  if (a === b) {
    return 1
  }
  const aWords = tokenizeWords(a).filter((t) => t.trim().length > 0)
  const bWords = tokenizeWords(b).filter((t) => t.trim().length > 0)
  if (aWords.length === 0 || bWords.length === 0) {
    return 0
  }

  const counts = new Map<string, number>()
  for (const word of aWords) {
    counts.set(word, (counts.get(word) ?? 0) + 1)
  }

  let shared = 0
  for (const word of bWords) {
    const remaining = counts.get(word) ?? 0
    if (remaining > 0) {
      counts.set(word, remaining - 1)
      shared++
    }
  }

  return (2 * shared) / (aWords.length + bWords.length)
}
